import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, MapPin, AlertTriangle, Building2, Loader2, ArrowUpRight, BarChart3 } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import AiStats from "@/components/AiStats";
import { SEV_META, ASSET_TYPES } from "@/lib/defects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const THUMBS = [
  "https://images.unsplash.com/photo-1727238451791-af16e7f7999b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

function StatCard({ label, value, accent, icon: Icon }) {
  return (
    <div className="border border-white/10 bg-[#121212] p-6" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <p className="label-caps text-muted-foreground">{label}</p>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="font-display text-4xl font-extrabold mt-3 tracking-tight">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", asset_type: "parking", address: "", description: "", matterport_model_id: "", job_number: "", client: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/projects"), api.get("/dashboard/stats")]);
    setProjects(p.data);
    setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Property name is required");
    setSaving(true);
    try {
      const thumb = THUMBS[(projects?.length || 0) % THUMBS.length];
      const { data } = await api.post("/projects", { ...form, thumbnail: thumb });
      toast.success("Property created");
      setOpen(false);
      setForm({ name: "", asset_type: "parking", address: "", description: "", matterport_model_id: "", job_number: "", client: "", contact_name: "", contact_email: "", contact_phone: "" });
      navigate(`/projects/${data.id}`);
    } catch (e) {
      toast.error("Could not create property");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="h-16 border-b border-white/10 flex items-center justify-between px-8 md:sticky md:top-0 bg-background/90 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Inspection Projects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-button" className="bg-[#C5A24A] hover:bg-[#A8863A] text-white gap-2 transition-colors">
              <Plus className="w-4 h-4" /> New Property
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-foreground sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">New Property</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps text-muted-foreground">Job number</Label>
                  <Input data-testid="project-job-input" value={form.job_number} onChange={(e) => setForm({ ...form, job_number: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="e.g. ZEN-2026-014" />
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">Client</Label>
                  <Input data-testid="project-client-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="Client / owner" />
                </div>
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Property name</Label>
                <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="e.g. Riverside Structure" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Address</Label>
                <Input data-testid="project-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="123 Main St" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Contact person</Label>
                <Input data-testid="project-contact-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="Full name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps text-muted-foreground">Contact email</Label>
                  <Input data-testid="project-contact-email-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="name@company.com" />
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">Contact phone</Label>
                  <Input data-testid="project-contact-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="(555) 555-5555" />
                </div>
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Asset type</Label>
                <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                  <SelectTrigger data-testid="project-asset-type" className="mt-1.5 bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1E1E1E] border-white/10">
                    {Object.keys(ASSET_TYPES).map((k) => <SelectItem key={k} value={k}>{ASSET_TYPES[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Matterport Model ID</Label>
                <Input data-testid="project-model-input" value={form.matterport_model_id} onChange={(e) => setForm({ ...form, matterport_model_id: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10 font-mono text-xs" placeholder="e.g. SxQL3iGyoDo — the m= code from your scan URL" />
              </div>
              <div>
                <Label className="label-caps text-muted-foreground">Notes</Label>
                <Textarea data-testid="project-notes-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" placeholder="Scope, engineer, date…" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="project-create-submit" onClick={create} disabled={saving} className="bg-[#C5A24A] hover:bg-[#A8863A] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & open"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-8 space-y-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Properties" value={stats.projects} accent="#C5A24A" icon={Building2} />
            <StatCard label="Total Defects" value={stats.defects} accent="#A1A1AA" icon={MapPin} />
            <StatCard label="Critical" value={stats.by_severity.critical} accent="#FF3B30" icon={AlertTriangle} />
            <StatCard label="High" value={stats.by_severity.high} accent="#FF9500" icon={AlertTriangle} />
          </div>
        )}

        <div className="border border-white/10 bg-[#121212] p-6" data-testid="dashboard-ai-performance">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[#C5A24A]" />
            <h2 className="font-display font-semibold">AI Performance</h2>
            <span className="text-xs text-muted-foreground">across all properties</span>
          </div>
          <AiStats />
        </div>

        {!projects ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-white/15 py-24 flex flex-col items-center text-center">
            <Building2 className="w-10 h-10 text-muted-foreground mb-4" />
            <p className="font-display text-lg font-semibold">No properties yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first property to start inspecting.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="group border border-white/10 bg-[#121212] overflow-hidden cursor-pointer hover:border-white/25 transition-colors"
              >
                <div className="relative h-40 overflow-hidden">
                  <img src={p.thumbnail || THUMBS[0]} alt={p.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold tracking-tight truncate">{p.name}</h3>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{p.address || "No address"}</p>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs px-2 py-1 bg-[#1E1E1E] border border-white/10 rounded-full">{p.defect_count} defects</span>
                    {p.matterport_model_id ? (
                      <span className="text-xs px-2 py-1 rounded-full" style={{ background: SEV_META.low.bg, color: SEV_META.low.color }}>Scan linked</span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full" style={{ background: SEV_META.medium.bg, color: SEV_META.medium.color }}>No scan</span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
